import { getGlobalConfig } from '../config';
import { CONSENT } from '@sklik/cmp2-common';
import { DOT_CONFIG_PROPERTIES, IDENTITIES_KEYS, IDENTITY_STATE, } from '../../types';
import { TIMEOUT } from '../consts';
import { createIdentityObject } from '../identities';
import { storeInvalidUpdates } from '../invalidUpdates';
import { isObject } from '../utils';
import { debug } from '../debug';
/**
 * Get consent string / consent object from login identity (if any)
 */
const _parseLoginConsent = (loginIdentity) => {
    const consentString = loginIdentity ? getLoginConsentString(loginIdentity) : null;
    const consentResult = loginIdentity ? getLoginConsentFormatedResult(consentString) : null;
    return { consentResult, consentString };
};
/**
 * Get LOGIN (window.login.current -> storage -> null)
 */
export const getLoginByPriority = (loginIdentity) => getExtendedLoginFromWindowObject() || loginIdentity || null;
/**
 * Get a shallow copy of `window.login.current` and `window.login.current.others`
 * if at least one parameter in `keys` is truthy
 */
export const getLoginObject = () => {
    var _a, _b, _c;
    const keys = ['advert_uid', 'uid'];
    const hasLoginId = keys.some((key) => { var _a, _b; return (_b = (_a = window.login) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b[key]; });
    const isLoginForgotten = ((_b = (_a = window.login) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.state) === 'forget';
    if (!((_c = window.login) === null || _c === void 0 ? void 0 : _c.current) || (!hasLoginId && !isLoginForgotten)) {
        return undefined;
    }
    const loginCopy = Object.assign({}, window.login.current);
    if (window.login.current.others) {
        loginCopy.others = [...window.login.current.others];
    }
    return loginCopy;
};
export const getBadgeFromDot = () => { var _a; return !!((_a = window.DOT) === null || _a === void 0 ? void 0 : _a.getCfgValue(DOT_CONFIG_PROPERTIES.badge)); };
/**
 * Get consent value from window.login object (copy "advert_uid" to "said", parse "consentString" from "euconsent")
 */
export const getExtendedLoginFromWindowObject = () => {
    const loginCurrent = getLoginObject();
    if (!loginCurrent) {
        return;
    }
    const output = loginCurrent;
    if (output.advert_uid) {
        output.said = output.advert_uid;
    }
    if (output.euconsent) {
        try {
            const parsedConsentString = getLoginConsentString(output);
            if (parsedConsentString) {
                output.consentString = parsedConsentString;
            }
        }
        catch (_a) {
            // no-op
        }
    }
    if (Array.isArray(output.others)) {
        output.others = output.others.map((o) => {
            return isObject(o) && o.advert_uid ? Object.assign(Object.assign({}, o), { said: o.advert_uid }) : o;
        });
    }
    return output;
};
/**
 * Add LOGIN (/CONSENT) to identitites object (if any)
 */
export const getLogin = (identities) => {
    const login = getExtendedLoginFromWindowObject();
    const getIdentityObject = (key, value) => {
        return createIdentityObject(identities[key], value, IDENTITY_STATE.fresh, true);
    };
    if (login) {
        identities[IDENTITIES_KEYS.login] = getIdentityObject(IDENTITIES_KEYS.login, login);
        const { consentResult } = _parseLoginConsent(login);
        if (consentResult) {
            debug({ message: 'LOGIN (sync): Setting CONSENT identity from LOGIN.euconsent', consentResult });
            identities[IDENTITIES_KEYS.consent] = getIdentityObject(IDENTITIES_KEYS.consent, consentResult);
        }
        else if ((login === null || login === void 0 ? void 0 : login.state) && login.state !== 'forget') {
            debug({ message: 'LOGIN (sync): Removing CONSENT identity because global LOGIN.euconsent missing' });
            identities[IDENTITIES_KEYS.consent] = getIdentityObject(IDENTITIES_KEYS.consent, null);
        }
    }
};
/**
 * Listen to events emited by BADGE
 */
export const listenToBadge = (updater, getter) => {
    var _a, _b;
    const handler = ({ loginTimeoutExceeded } = {}) => {
        var _a;
        const existsInStorage = getter(IDENTITIES_KEYS.login);
        const login = getExtendedLoginFromWindowObject();
        // "login" (Badge) might be undefined because Badge is not used on every external service
        //  we need to check first if "window.login" created by Badge is present
        if (window.login && !login && loginTimeoutExceeded) {
            storeInvalidUpdates({
                identity: IDENTITIES_KEYS.login,
                type: 'timeout',
                timestamp: Date.now(),
            });
        }
        if (login || existsInStorage) {
            const value = (login || existsInStorage.value);
            const state = login ? IDENTITY_STATE.fresh : existsInStorage.state;
            const updated = { login: { value, state } };
            const newUid = login === null || login === void 0 ? void 0 : login.advert_uid;
            const oldUid = (_a = existsInStorage === null || existsInStorage === void 0 ? void 0 : existsInStorage.value) === null || _a === void 0 ? void 0 : _a.advert_uid;
            // "rusId" changed?  wipe out CONSENT by default | wipe out ID5 if newUid present
            if (newUid && newUid !== oldUid) {
                debug({ message: 'LOGIN: Removing CONSENT identity because LOGIN.uid changed' });
                updated.consent = { value: null, state: IDENTITY_STATE.fresh };
                updated.id5 = { value: null, state: IDENTITY_STATE.fresh };
            }
            const { consentResult } = _parseLoginConsent(login);
            // LOGIN with CONSENT present -> write CONSENT
            if (consentResult) {
                debug({
                    message: 'LOGIN (async): Setting CONSENT identity from window.login.current.euconsent',
                    consentResult,
                });
                updated.consent = {
                    value: consentResult,
                    state: IDENTITY_STATE.fresh,
                };
            }
            else if ((login === null || login === void 0 ? void 0 : login.state) && login.state !== 'forget') {
                debug({ message: 'LOGIN: Removing CONSENT identity because global LOGIN.consent missing' });
                updated.consent = { value: null, state: IDENTITY_STATE.fresh };
            }
            updater(updated);
        }
    };
    const loginTimer = window.setTimeout(() => {
        handler({ loginTimeoutExceeded: true });
    }, ((_b = (_a = getGlobalConfig()) === null || _a === void 0 ? void 0 : _a.timeouts) === null || _b === void 0 ? void 0 : _b[IDENTITIES_KEYS.login]) || TIMEOUT);
    const done = () => {
        window.clearTimeout(loginTimer);
        handler();
    };
    window.addEventListener('badge', done);
    window.addEventListener('login', done);
    window.addEventListener('logout', done);
    window.addEventListener('forget', done);
};
/**
 * Get parsed euconsent object from login identity
 */
export const getLoginConsentString = (loginIdentity) => {
    var _a, _b;
    let loginConsent = null;
    if (loginIdentity) {
        try {
            loginConsent = (_b = (_a = JSON.parse(loginIdentity.euconsent)) === null || _a === void 0 ? void 0 : _a.v2) === null || _b === void 0 ? void 0 : _b.encodedCookie;
        }
        catch (_c) {
            // no-op
        }
    }
    return loginConsent;
};
/**
 * Gets ConsentResult object from "login" identity (mirroring format from "cmp2-common")
 */
export const getLoginConsentFormatedResult = (loginConsentString) => {
    if (!loginConsentString) {
        return null;
    }
    return {
        version: 'v2',
        name: `euconsent-v2`,
        consentString: loginConsentString,
        purposeOne: CONSENT.hasPurposeOne(loginConsentString),
    };
};
